<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import Translator from "@/utils/common/Translator.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import ChatPresenter from "@/utils/presenters/ChatPresenter.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import DialogCloseIcon from "@/assets/editViewIcons/DialogCloseIcon.vue";
import EntityReferenceData = namaTypes.EntityReferenceData;
import DTOChatParticipant = namaTypes.DTOChatParticipant;

const props = defineProps<{
  presenter: ChatPresenter;
  participants: DTOChatParticipant[];
}>();

const emit = defineEmits<{
  close: [];
  added: [];
}>();

const searchQuery = ref('');
const availableUsers = ref<EntityReferenceData[]>([]);
const selectedUsers = ref<EntityReferenceData[]>([]);
const loading = ref(false);
const adding = ref(false);

const filteredUsers = computed(() => {
  const existingIds = new Set(props.participants.map(p => p.participant?.id));
  let users = availableUsers.value.filter(u => !existingIds.has(u.id));
  const search = searchQuery.value?.toLowerCase()?.trim();
  if (search) {
    users = users.filter(u => {
      const fields = [u.name1, u.name2, u.code, u.actualCode];
      return fields.some(f => f?.toString().toLowerCase().includes(search));
    });
  }
  return users;
});

const selectedIds = computed(() => new Set(selectedUsers.value.map(u => u.id)));

onMounted(async () => {
  loading.value = true;
  availableUsers.value = await props.presenter.fetchUsers();
  loading.value = false;
});

function toggleUser(user: EntityReferenceData) {
  const idx = selectedUsers.value.findIndex(u => u.id === user.id);
  if (idx >= 0) {
    selectedUsers.value.splice(idx, 1);
  } else {
    selectedUsers.value.push(user);
  }
}

function removeSelected(userId: string) {
  selectedUsers.value = selectedUsers.value.filter(u => u.id !== userId);
}

async function addSelectedMembers() {
  if (adding.value || selectedUsers.value.length === 0) return;
  adding.value = true;
  for (const user of selectedUsers.value) {
    await props.presenter.addParticipant(user);
  }
  adding.value = false;
  selectedUsers.value = [];
  emit('added');
}
</script>

<template>
  <q-card class="tw-shadow-none dark:tw-text-white dark:tw-bg-namaBackground-deep-dark tw-rounded-2xl tw-bg-namaBackground-deep-light tw-w-fit">
    <q-bar v-if="Translator.translateId('chatAddUser')" class="tw-text-xs tw-text-primary-light dark:tw-text-primary-dark tw-h-10">
      <span class="tw-text-sm tw-font-semibold tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark"
            v-text="Translator.translateId('chatAddUser')"/>
      <q-space/>
      <q-avatar square @click="emit('close')" class="nama-avatar-hover tw-cursor-pointer">
        <DialogCloseIcon class="tw-size-4"/>
      </q-avatar>
    </q-bar>
    <div class="tw-flex tw-flex-col tw-h-[70vh] tw-max-h-[70vh] tw-w-80
                tw-bg-namaBackground-light dark:tw-bg-namaBackground-dark tw-overflow-hidden">
      <div v-if="selectedUsers.length > 0" class="tw-px-3 tw-pt-2 tw-shrink-0">
        <div class="tw-flex tw-flex-wrap tw-gap-1.5">
          <span v-for="user in selectedUsers" :key="user.id"
                class="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs
                       tw-bg-primary-light/10 dark:tw-bg-primary-dark/10 tw-text-primary-light dark:tw-text-primary-dark">
            {{ Translator.translateRef(user) }}
            <NamaIcon defaultIcon="close" class="!tw-text-xs tw-cursor-pointer" @click="removeSelected(user.id ?? '')"/>
          </span>
        </div>
      </div>
      <div class="tw-px-3 tw-py-2 tw-shrink-0">
        <input v-model="searchQuery"
               :placeholder="Translator.translateId('search')"
               class="tw-w-full tw-border tw-border-gray-300 dark:tw-border-gray-600 tw-rounded-md tw-px-3 tw-py-1.5 tw-text-sm
                      tw-bg-namaBackground-deep-light dark:tw-bg-namaBackground-deep-dark tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark
                      tw-outline-none focus:tw-border-primary-light dark:focus:tw-border-primary-dark"
               autofocus/>
      </div>
      <div class="tw-flex-1 tw-overflow-y-auto tw-px-1">
        <div v-if="loading" class="tw-flex tw-justify-center tw-py-6">
          <q-spinner-dots size="2rem" color="primary"/>
        </div>
        <div v-else-if="filteredUsers.length === 0"
             class="tw-text-center tw-py-6 tw-text-sm tw-text-gray-400"
             v-text="Translator.translateId('mentionNoResults')"/>
        <div v-else v-for="user in filteredUsers" :key="user.id"
             @click="toggleUser(user)"
             class="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-cursor-pointer tw-rounded-md tw-transition-colors
                    hover:tw-bg-gray-100 dark:hover:tw-bg-gray-700/50"
             :class="{'tw-bg-primary-light/5 dark:tw-bg-primary-dark/5': selectedIds.has(user.id)}">
          <q-checkbox :model-value="selectedIds.has(user.id)" size="sm" dense
                      @update:model-value="toggleUser(user)" color="primary" class="tw-pointer-events-none"/>
          <q-avatar size="36px">
            <img :src="LoginUtils.getUserImageSrc(user.id ?? '', false)" alt="">
          </q-avatar>
          <span class="tw-flex-1 tw-text-sm tw-text-namaFontColor-light dark:tw-text-namaFontColor-dark tw-truncate"
                v-text="Translator.translateRef(user)"/>
        </div>
      </div>
      <div v-if="selectedUsers.length > 0" class="tw-px-3 tw-py-2 tw-shrink-0 tw-border-t tw-border-gray-200 dark:tw-border-gray-700">
        <q-btn unelevated no-caps dense color="primary" class="tw-w-full tw-rounded-md tw-py-1.5"
               :loading="adding" @click="addSelectedMembers" :title="Translator.translateId('chatAddUser') + ' ' + selectedUsers.length ">
          <span v-text="Translator.translateId('chatAddUser') + ' ' + selectedUsers.length"/>
        </q-btn>
      </div>
    </div>
  </q-card>
</template>
